import { useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

function matchesStaffQuery(person, query) {
  if (!query.trim()) return true;
  const q = query.trim().toLowerCase();
  return (
    (person.name || "").toLowerCase().includes(q) ||
    (person.qid || "").toLowerCase().includes(q) ||
    (person.phone || "").includes(q)
  );
}

/**
 * Searchable staff picker — filters by name, QID, or phone.
 * Use `onSelect` for multi-add flows; use `value` + `onValueChange` for single select.
 */
export default function StaffSearchSelect({
  staff = [],
  excludeQids = [],
  value,
  onValueChange,
  onSelect,
  placeholder = "Search staff by name or QID…",
  triggerLabel = "Select staff",
  disabled = false,
  className,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const excluded = new Set(excludeQids);
  const options = staff.filter((s) => !excluded.has(s.qid));
  const selected = staff.find((s) => s.qid === value);

  const pick = (qid) => {
    if (onSelect) {
      onSelect(qid);
      setQuery("");
      setOpen(false);
      return;
    }
    onValueChange?.(qid);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            "mt-1.5 w-full justify-between font-normal text-left h-10",
            !selected && !onSelect && "text-muted-foreground",
            className
          )}
        >
          <span className="truncate">
            {selected ? `${selected.name} (${selected.qid})` : triggerLabel}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={placeholder}
            value={query}
            onValueChange={setQuery}
          />
          <CommandList>
            <CommandEmpty>No staff found.</CommandEmpty>
            <CommandGroup>
              {options
                .filter((person) => matchesStaffQuery(person, query))
                .map((person) => (
                  <CommandItem
                    key={person.qid}
                    value={person.qid}
                    onSelect={() => pick(person.qid)}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        value === person.qid ? "opacity-100" : "opacity-0"
                      )}
                    />
                    <div className="min-w-0">
                      <div className="truncate font-medium">{person.name}</div>
                      <div className="truncate text-xs text-muted-foreground">{person.qid}</div>
                    </div>
                  </CommandItem>
                ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
